/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import type {
  AccessOverridesData,
  AccessOverridesStorageObject
} from '../../../models/AccessOverrides';
import type {FlagValue} from '../../../models/LaunchDarkly';
import type {FulfillableItems} from '../../../models/Context';
import {
  mergeFeatureFlagsForShell,
  mergeFulfillableItemsForShell
} from './common';
import {storage} from '@exc/storage';

interface AppAssemblyService {
  setFeatureFlags: (featureflags: Record<string, FlagValue>) => void;
  setFulfillableItems: (fis: FulfillableItems) => void;
}

export interface SetAccessOverrideParameters {
  appAssemblyService?: AppAssemblyService;
  enabledState: boolean;
  hashedAuthId: string;
  featureFlags?: Record<string, string>;
  fiServiceCode?: string;
  selectedImsOrg: string;
  userFulfillableItems: FulfillableItems;
}

export const setAccessOverrides = async ({
  appAssemblyService,
  enabledState,
  featureFlags,
  fiServiceCode,
  hashedAuthId,
  selectedImsOrg,
  userFulfillableItems
}: SetAccessOverrideParameters) => {
  // Check storage for enablement and data (need for page refresh)
  const storedOverrides = await storage.local.get<AccessOverridesStorageObject>('accessOverrides');
  const userOverrides: AccessOverridesData = storedOverrides?.[hashedAuthId] || {featureFlags: {}, fulfillableItems: {}};
  // We only implement the overrides if they are currently enabled (toggle is on)
  if (enabledState || userOverrides.enableOverrides) {
    const {featureFlags: featureFlagsOverride, fulfillableItems} = userOverrides;
    // Feature flags - Requires featureFlags from LaunchDarkly in state
    const activeFeatureFlags = featureFlags && mergeFeatureFlagsForShell(
      featureFlagsOverride['unified-shell'], featureFlags
    );
    activeFeatureFlags && appAssemblyService?.setFeatureFlags(activeFeatureFlags);
    // Fulfillable items - Requires a fiServiceCode, indicating we're in a solution
    // that uses fulfillable items
    const activeFulfillableItems = fiServiceCode && mergeFulfillableItemsForShell(
      selectedImsOrg,
      fiServiceCode,
      userFulfillableItems,
      fulfillableItems[fiServiceCode]
    );
    activeFulfillableItems && appAssemblyService?.setFulfillableItems(activeFulfillableItems);
    return {
      accessOverridesEnabled: true,
      activeFeatureFlags: activeFeatureFlags || featureFlags || {},
      activeFulfillableItems: activeFulfillableItems || userFulfillableItems
    };
  }
};
